import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { drobek } from 'drobek';
import { labelOf, PixelScene, skyOf, type Place } from './scene';
import './styles.css';

const FRESH_MINUTES = 45;
const EVERY_MINUTES = 15;

type Weather = {
  latitude: number;
  longitude: number;
  current: { time: string; temperature_2m?: number; wind_speed_10m?: number; weather_code?: number };
};
type Release = { id: number; tag_name: string; name?: string; html_url?: string; published_at: string; prerelease?: boolean };
type Row<T> = T & { _id: string; _created_at: string; _updated_at: string };

const weather = drobek.data.collection<Weather>('weather');
const releases = drobek.data.collection<Release>('releases');

const ZONES: Record<Place, string> = { Espoo: 'Europe/Helsinki', Brno: 'Europe/Prague' };

function placeOf(w: Weather): Place {
  return w.latitude > 55 ? 'Espoo' : 'Brno';
}

function parseUtc(iso: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
}

function minutesSince(iso: string, now: number): number {
  const t = parseUtc(iso);
  return Number.isNaN(t) ? Infinity : Math.max(0, Math.round((now - t) / 60000));
}

function ago(minutes: number): string {
  if (!Number.isFinite(minutes)) return 'an unknown time ago';
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const h = Math.floor(minutes / 60);
  return h < 48 ? `${h} h ${minutes % 60} min ago` : `${Math.floor(h / 24)} days ago`;
}

function clock(t: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone, timeZoneName: 'short' }).format(t);
}

function isDay(t: number, place: Place): boolean {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: ZONES[place] }).format(t));
  return h >= 7 && h < 19;
}

function nextImportText(nextImport: number, now: number): string {
  const minutes = Math.round((nextImport - now) / 60000);
  if (minutes <= 1) return 'Next import any minute now.';
  return `Next import in about ${minutes} minutes.`;
}

function PlaceCard({ w, now }: { w: Row<Weather>; now: number }) {
  const place = placeOf(w);
  const t = parseUtc(w.current.time);
  const sky = skyOf(w.current.weather_code);
  const day = Number.isNaN(t) ? true : isDay(t, place);
  const label = labelOf(w.current.weather_code);
  const temp = w.current.temperature_2m;
  return (
    <article className="place">
      <PixelScene place={place} sky={sky} day={day} label={`${label} over ${place}, ${day ? 'daytime' : 'night'}`} />
      <div className="readout">
        <div className="readout-head">
          <h2>{place}</h2>
          <p className="temp">{temp === undefined ? '–' : `${Math.round(temp)}°`}</p>
        </div>
        <p className="cond">
          {label}
          {w.current.wind_speed_10m !== undefined && <>, wind {Math.round(w.current.wind_speed_10m)} km/h</>}
        </p>
        <p className="times">
          Measured at {Number.isNaN(t) ? '–' : clock(t, ZONES[place])} ({ago(minutesSince(w.current.time, now))})
        </p>
      </div>
    </article>
  );
}

function App() {
  const [rows, setRows] = useState<Row<Weather>[] | null>(null);
  const [rels, setRels] = useState<Row<Release>[] | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(() => {
    Promise.all([weather.list({ limit: 10 }), releases.list({ sort: 'published_at', dir: 'desc', limit: 5 })])
      .then(([wp, rp]) => {
        setRows(wp.records as Row<Weather>[]);
        setRels(rp.records as Row<Release>[]);
        setError('');
        setNow(Date.now());
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'The synced records could not be loaded.'));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const sorted = [...(rows ?? [])].sort((a, b) => b.latitude - a.latitude);
  const freshest = sorted.length ? Math.min(...sorted.map((w) => minutesSince(w.current.time, now))) : Infinity;
  const ok = sorted.length > 0 && freshest <= FRESH_MINUTES;
  const lastWrite = sorted.length ? Math.max(...sorted.map((w) => parseUtc(w._updated_at))) : NaN;
  const nextImport = Number.isNaN(lastWrite) ? NaN : lastWrite + EVERY_MINUTES * 60000;

  return (
    <main>
      <header className="top">
        <h1>Sync canary</h1>
        <p className="lead">
          Live weather in Espoo and Brno, imported by drobek's <code>sync</code> module every {EVERY_MINUTES} minutes. drobek fetches the
          weather API on a schedule and stores the answer; this page only reads the stored records.
        </p>
        {rows !== null && (
          <p className={`status ${ok ? 'is-ok' : 'is-stale'}`} data-status={ok ? 'ok' : 'stale'} role="status">
            <span className="led" aria-hidden="true" />
            <span>
              {sorted.length === 0
                ? 'No readings yet: the first import has not run.'
                : ok
                  ? `Fresh: the latest reading is from ${ago(freshest)}.`
                  : `Stale: the latest reading is from ${ago(freshest)}, over ${FRESH_MINUTES} minutes. The schedule may be stuck.`}
              {ok && !Number.isNaN(nextImport) && <span className="next"> {nextImportText(nextImport, now)}</span>}
            </span>
          </p>
        )}
      </header>

      {error && (
        <p role="alert" className="alert">
          {error} Reload the page to try again.
        </p>
      )}

      {rows === null && !error && (
        <p className="loading" aria-busy="true">
          Loading the latest readings…
        </p>
      )}

      {sorted.length > 0 && (
        <section className="places" aria-label="Current weather">
          {sorted.map((w) => (
            <PlaceCard key={w._id} w={w} now={now} />
          ))}
        </section>
      )}

      <section className="pipeline" aria-labelledby="pipeline-h">
        <h2 id="pipeline-h">How the data gets here</h2>
        <ol>
          <li>
            <strong>Open-Meteo</strong> answers with the current weather for both cities.
          </li>
          <li>
            The <strong>sync</strong> module calls it through a proxy upstream every {EVERY_MINUTES} minutes.
          </li>
          <li>
            The answer replaces the records of the <strong>weather</strong> collection.
          </li>
          <li>
            This page reads them with <code>drobek.data</code> and refreshes every minute.
          </li>
        </ol>
      </section>

      <section className="releases" aria-labelledby="releases-h">
        <h2 id="releases-h">drobek releases</h2>
        <p className="note">A second source: GitHub's release list, imported every hour and updated by release id.</p>
        {rels !== null &&
          (rels.length === 0 ? (
            <p className="empty">No releases imported yet. The import runs every hour.</p>
          ) : (
            <ul>
              {rels.map((r) => (
                <li key={r._id}>
                  <a href={r.html_url} rel="noreferrer">
                    {r.tag_name}
                  </a>
                  <span className="rel-date">{r.published_at.slice(0, 10)}</span>
                  {r.prerelease && <span className="rel-pre">pre-release</span>}
                </li>
              ))}
            </ul>
          ))}
      </section>

      <footer>
        Weather data by <a href="https://open-meteo.com/">Open-Meteo.com</a> (CC BY 4.0). Releases from GitHub. Built and hosted on drobek.
      </footer>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
